/**
 * useCoPilotBrainCore Hook — STREAMING, SHARED BY BOTH PORTALS.
 *
 * Built on the shared headless conversation state machine
 * (`@tonnie/copilot/client`) + the SDK streaming client
 * (`CoPilotService.streamInteract`). Tokens render live; response envelopes,
 * proposals, suggestions, degraded/error states all flow through the ONE
 * reducer every portal shares.
 *
 * This was journey-web's `useCoPilotBrain`, lifted here so business-web gets the
 * identical behaviour instead of a thinner fork. The three journey-only reads
 * (auth context, page-context provider, hard-coded citizen file-action ids) are
 * now **injected options**, so each app supplies its own and both get the same
 * returned surface.
 */

import {
  CoPilotService,
  useApiClient,
  useBrainStatusSince,
  useCoPilotQuota,
  useCoPilotCapabilities,
  useCoPilotFeedback,
  type CoPilotMessage as SdkHistoryMessage,
  type CoPilotQuota,
  type StreamingFetch,
} from '@tonnie/sdk';
import {
  conversationSafetyBanner,
  initialConversationState,
  reduceConversation,
  runConversationTurn,
  setMessageFeedback,
  dismissProposal as dismissProposalState,
  appendAssistantNotice,
  loadConversation,
  COPILOT_CLIENT_ERROR_KEY,
  type CoPilotConversationState,
  type CoPilotMessage,
  type CoPilotToolActivity,
} from '@tonnie/copilot/client';
import type {
  CoPilotActionProposal,
  CoPilotAttachmentKind,
  SafetyBannerConfig,
} from '@tonnie/types';
import type { CoPilotDomain } from '@tonnie/copilot/domains';
import { useTranslation, useLocale } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ICoPilotBrainProps } from './brain.types';

/** The turn context each portal resolves for itself (page/hero/entity). */
export interface CoPilotTurnContext {
  /** The CoPilot domain of the page (a registry key). */
  hero?: CoPilotDomain;
  screen?: string;
  portal?: 'journey' | 'citizen' | 'business';
  entityId?: string;
  entityType?: string;
  /**
   * The journey on screen: the citizen's current journey, or the journey of the
   * company's open talent drawer. The Companion grounds and acts on it (X6/X8).
   */
  journeyInstanceId?: string;
}

/** Host-injected wiring — everything that differs between the two portals. */
export interface IUseCoPilotBrainCoreOptions {
  /**
   * Per-turn context, resolved by the host on each send. Journey supplies its
   * CoPilotContextProvider `pageContext`; business supplies the open talent
   * (`pageEntity`) + `portal:'business'`.
   */
  resolveContext: () => CoPilotTurnContext;
  /** Flush host-queued analytics before a turn (journey's host events). */
  flushPendingEvents?: () => void;
  /** Current user id — analytics only. */
  userId?: string;
  /**
   * Analytics sink — INJECTED, not imported. The SDK's `useTrackEvent` requires
   * an AnalyticsProvider that journey-web mounts but business-web does not, so
   * calling it inside this shared hook crashed the whole business Companion.
   * The host that has the provider passes its tracker; the other passes nothing.
   */
  trackEvent?: (event: string, props: Record<string, unknown>) => void;
  /**
   * Action ids that CONSUME the composer's attachment, so a successful confirm
   * clears it. Citizen: attach-to-task / save-to-Vault. Business: the vetting
   * document push. Injected because the catalog differs per portal.
   */
  fileActionIds?: readonly string[];
  /**
   * The fetch used for the SSE turn. Web omits it (global `fetch` streams
   * fine). **React Native MUST pass one** — RN's global fetch buffers the whole
   * body, so streaming silently dies; `expo/fetch` exposes a real
   * `ReadableStream` (see citizen-mobile `core/lib/copilot-stream.ts`).
   */
  streamingFetch?: StreamingFetch;
  /**
   * Where the answer-depth preference persists. Web passes a `localStorage`
   * adapter, React Native passes AsyncStorage. Async on purpose so both fit;
   * omitted → depth still works, just per-session.
   */
  storage?: CoPilotCoreStorage;
  /**
   * How a picked file is uploaded. Web uses the SDK's DOM-`File` methods;
   * React Native cannot (appending a `{uri}` part to FormData fails at the
   * native bridge) and injects an `expo-file-system` UploadTask uploader.
   */
  uploader?: CoPilotCoreUploader;
}

/** Minimal async key-value store — localStorage on web, AsyncStorage on RN. */
export interface CoPilotCoreStorage {
  get(key: string): Promise<string | null> | string | null;
  set(key: string, value: string): Promise<void> | void;
}

/** Platform uploader: takes the picked file, returns the ingested attachment. */
export type CoPilotCoreUploader = (
  file: unknown,
  kind: CoPilotAttachmentKind
) => Promise<{
  attachmentId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  kind: CoPilotAttachmentKind;
  text: string;
}>;

/** A file attached to the composer, awaiting the user's send. */
export interface PendingAttachment {
  storageKey: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  kind: CoPilotAttachmentKind;
  /** Extracted text/transcript/description — sent with the turn as data. */
  text: string;
}

/** Answer lengths/depths the user can pick (persisted, sent with each turn), in toolbar order. */
export const RESPONSE_DEPTHS = ['brief', 'balanced', 'detailed'] as const;
export type ResponseDepth = (typeof RESPONSE_DEPTHS)[number];
const DEPTH_STORAGE_KEY = 'tonnie.copilot.responseDepth';

/** Persisted value → a valid depth (anything unrecognised falls back). */
function normalizeDepth(value: string | null | undefined): ResponseDepth {
  return RESPONSE_DEPTHS.find((depth) => depth === value) ?? 'balanced';
}

export interface IUseCoPilotBrainReturn {
  messages: CoPilotMessage[];
  input: string;
  setInput: (value: string) => void;
  handleSend: () => void;
  /** Send arbitrary text as a turn (choice chips call this). */
  sendMessage: (text: string) => void;
  /**
   * A question asked from the page the person is on ("Ask the CoPilot" on a
   * card): sent as their turn once the conversation the panel opens with is
   * read — never overwritten by the history it continues.
   */
  ask: (text: string) => void;
  /** A file attached to the composer, awaiting send (null when none). */
  pendingAttachment: PendingAttachment | null;
  /** Remove the attached file before sending. */
  clearPendingAttachment: () => void;
  handleSuggestionClick: (suggestion: string) => void;
  handleFeedback: (messageId: string, feedback: 'up' | 'down') => void;
  /** Clear the conversation + server session and start fresh. */
  handleClearConversation: () => void;
  /**
   * The conversation currently open (0089 history), or null for a brand-new
   * unsent chat. The history panel highlights the matching row.
   */
  currentConversationId: string | null;
  /** Open a past conversation and continue it (fetches + seeds its messages). */
  selectConversation: (id: string) => Promise<void>;
  /** Start a fresh chat — CLIENT-ONLY: no server row until the first message. */
  startNewConversation: () => void;
  /** True while a selected conversation's messages are loading. */
  isConversationLoading: boolean;
  /** The chosen answer length/depth (persisted). */
  responseDepth: ResponseDepth;
  /** Change the answer length/depth (persists + applies to the next turn). */
  setResponseDepth: (depth: ResponseDepth) => void;
  /** Whether the composer's "Search the web" toggle is on (per-session, BL-213 v2). */
  webSearchEnabled: boolean;
  /** Turn web-search consent on/off; sent as per-turn consent while on. */
  setWebSearchEnabled: (on: boolean) => void;
  /** Pending Tier-1/2 proposals awaiting the user's confirm/dismiss. */
  proposals: CoPilotActionProposal[];
  /** Confirm + execute a proposal (typedToken for guarded actions). Returns the outcome. */
  handleConfirmProposal: (
    proposalId: string,
    typedToken?: string
  ) => Promise<'success' | 'stale' | 'error'>;
  handleDismissProposal: (proposalId: string) => void;
  /** Upload a file (voice note / image / document) → send its text as an untrusted turn. */
  handleUpload: (file: unknown, kind: CoPilotAttachmentKind) => void;
  /** Whether the uploads capability is on (kill switch) — gates the attach UI. */
  uploadsEnabled: boolean;
  isLoading: boolean;
  /** i18n key for a typed stream error, or null. */
  errorKey: string | null;
  /** Correlation id of a failed turn — shown as a support "ref" + logged. */
  errorTraceId: string | null;
  /** DEV-only raw cause (omitted in prod) — surfaced for developers. */
  errorDetail: string | null;
  degraded: boolean;
  /**
   * The AI service is not answering (Journey PRD § Brain API Failures): the
   * last turn failed `provider_unavailable` and the brain's health read
   * confirms it is down. The panel then says so calmly — some features are
   * temporarily unavailable, we keep trying — in place of the turn's error,
   * and the engine asks again until the brain answers.
   */
  aiUnavailable: boolean;
  /** Ask the brain's health again now ("Try again" on that notice). */
  recheckAi: () => void;
  /** True while that question is out. */
  recheckingAi: boolean;
  quota: CoPilotQuota | undefined;
  /**
   * The tool the Companion is running RIGHT NOW (e.g. web search), or null.
   * Surfaced as a live "Searching the web…" affordance so a tool turn's
   * pre-prose wait reads as progress, not a hang (BL-217).
   */
  toolActivity: CoPilotToolActivity | null;
  /**
   * The safety banner to show (D2): the latest turn's, as the server built it,
   * or the standard one. A critical banner carries the crisis lines for the
   * person's country (`escalationAction`) — every client offers them.
   */
  safetyBanner: SafetyBannerConfig;
}

/** Map persisted history rows into the shared client message shape. */
function historyToMessages(rows: SdkHistoryMessage[]): CoPilotMessage[] {
  return rows.map((m) => ({
    id: m.id,
    role: m.role === 'user' ? 'user' : 'assistant',
    text: m.content,
    envelopes: [],
    streaming: false,
    feedback: null,
  }));
}

/** The banner a past thread's last answer was shown with, or null with none. */
function lastAnswerBanner(rows: SdkHistoryMessage[]): SafetyBannerConfig | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i];
    if (row.role === 'assistant') return row.safetyBanner ?? null;
  }
  return null;
}

/**
 * Default (web) upload path: the SDK's DOM-`File` endpoints. `video` has no DOM
 * caller — it is a mobile-only capture kind — so it is rejected rather than
 * silently mis-routed to the document extractor.
 */
async function uploadViaDomFile(service: CoPilotService, file: File, kind: CoPilotAttachmentKind) {
  switch (kind) {
    case 'audio':
      return service.uploadAudio(file);
    case 'image':
      return service.uploadImage(file);
    case 'document':
      return service.uploadDocument(file);
    case 'video':
      throw new Error('Video upload requires a host-injected uploader (React Native only).');
  }
}

export function useCoPilotBrainCore(
  context: ICoPilotBrainProps['context'],
  isOpen: boolean,
  options: IUseCoPilotBrainCoreOptions
): IUseCoPilotBrainReturn {
  const { t } = useTranslation();
  // The app's LIVE locale, sent with every turn so the CoPilot ALWAYS replies in
  // the app's configured language (never the language the user typed). Kept in a
  // ref (like depthRef below) so runTurn reads the latest without re-creating.
  // Omitted until the provider has resolved (mobile's async provider starts at
  // 'en') so a transient default can't pin a turn to English — the server
  // backstops with the persisted users.locale.
  const { locale: appLocale, isInitialized: localeReady } = useLocale();
  const localeRef = useRef(appLocale);
  localeRef.current = appLocale;
  const localeReadyRef = useRef(localeReady);
  localeReadyRef.current = localeReady;
  const { captureException } = useErrorCapture();
  const {
    resolveContext,
    flushPendingEvents,
    userId,
    fileActionIds,
    trackEvent,
    streamingFetch,
    storage,
    uploader,
  } = options;
  // Which confirmed actions consume the composer's attachment (so a successful
  // confirm clears it). Portal-specific — injected by the host.
  const fileActions = useMemo(() => new Set(fileActionIds ?? []), [fileActionIds]);

  const client = useApiClient();
  const service = useMemo(() => new CoPilotService(client), [client]);
  const queryClient = useQueryClient();

  // The engine is mounted at the app root now (0089) so the conversation
  // survives navigation — but that means these server reads would otherwise
  // fire at app mount, BEFORE Clerk auth is ready, and 401 (leaving quota
  // undefined → the footer wrongly shows "0 remaining"). Defer every server
  // read until the panel has been opened at least once (always post-auth), the
  // same timing the pre-hoist engine had. Sticky so a close doesn't drop it.
  const [panelActivated, setPanelActivated] = useState(false);
  useEffect(() => {
    if (isOpen) setPanelActivated(true);
  }, [isOpen]);

  const { data: quota } = useCoPilotQuota({ enabled: panelActivated });
  const { data: capabilities } = useCoPilotCapabilities({ enabled: panelActivated });
  const feedbackMutation = useCoPilotFeedback();

  const [conversation, setConversation] =
    useState<CoPilotConversationState>(initialConversationState);

  // A turn that failed because the AI service did not answer is checked
  // against the brain's health from that moment on; until the brain answers
  // again the panel shows the calm "temporarily unavailable" notice. Keyed on
  // the failed turn's trace id, so each such failure asks afresh, and any
  // other outcome (a new turn, another error) stops asking.
  const [unavailableSince, setUnavailableSince] = useState<number | null>(null);
  useEffect(() => {
    setUnavailableSince(conversation.errorCode === 'provider_unavailable' ? Date.now() : null);
  }, [conversation.errorCode, conversation.errorTraceId]);
  const brainStatus = useBrainStatusSince(unavailableSince);
  const [input, setInput] = useState('');
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // True while a picked past conversation's messages are being fetched.
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  // Answer length/depth (Claude "Styles" pattern), persisted by the HOST's
  // storage adapter (localStorage on web, AsyncStorage on RN) and sent with
  // every turn. A SYNCHRONOUS adapter resolves in the lazy initializer, so web
  // renders the saved depth on the first paint with no flash; an ASYNC one
  // (AsyncStorage) starts at the default and is adopted by the effect below.
  const storageRef = useRef(storage);
  storageRef.current = storage;
  const [responseDepth, setResponseDepthState] = useState<ResponseDepth>(() => {
    try {
      const saved = storage?.get(DEPTH_STORAGE_KEY);
      return saved instanceof Promise ? 'balanced' : normalizeDepth(saved);
    } catch {
      return 'balanced';
    }
  });
  const depthRef = useRef<ResponseDepth>(responseDepth);
  depthRef.current = responseDepth;
  // Async adapters only: adopt the persisted value once it arrives, unless the
  // user already picked one while it was in flight.
  const depthHydratedRef = useRef(false);
  const depthTouchedRef = useRef(false);
  useEffect(() => {
    if (depthHydratedRef.current) return;
    depthHydratedRef.current = true;
    const saved = storageRef.current?.get(DEPTH_STORAGE_KEY);
    if (!(saved instanceof Promise)) return;
    let cancelled = false;
    void saved
      .then((value) => {
        if (!cancelled && !depthTouchedRef.current) setResponseDepthState(normalizeDepth(value));
      })
      .catch(() => {
        /* storage unavailable — the in-memory default stands */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const setResponseDepth = useCallback((d: ResponseDepth) => {
    depthTouchedRef.current = true;
    setResponseDepthState(d);
    try {
      void Promise.resolve(storageRef.current?.set(DEPTH_STORAGE_KEY, d)).catch(() => {
        /* private mode / storage disabled — in-memory is fine */
      });
    } catch {
      /* private mode / storage disabled — in-memory is fine */
    }
  }, []);
  // Web-search consent (BL-213 v2). A per-session toggle (the composer globe),
  // OFF by default — privacy-preserving, never persisted — and sent as per-turn
  // consent while on, so the Companion may reach the live web only when the user
  // has explicitly enabled it. Mirrored to a ref so `runTurn` reads the latest.
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const webSearchRef = useRef(webSearchEnabled);
  webSearchRef.current = webSearchEnabled;

  const abortRef = useRef<AbortController | null>(null);
  // Mirror the latest state so `handleSend` reads it without a stale closure
  // and without depending on `conversation` (which would re-create the cb).
  const conversationRef = useRef(conversation);
  const applyState = useCallback((next: CoPilotConversationState) => {
    conversationRef.current = next;
    setConversation(next);
  }, []);

  // The greeting bubble for a brand-new chat (no server row yet).
  const makeGreeting = useCallback(
    (): CoPilotMessage => ({
      id: 'greeting',
      role: 'assistant',
      text: t('copilot.greeting', { context: '' }).replace('{{context}}', ''),
      envelopes: [],
      streaming: false,
      feedback: null,
    }),
    [t]
  );

  // Open a past conversation and continue it (0089). Aborts any in-flight turn
  // so its tokens can't land in the newly-selected thread, fetches the chosen
  // conversation's messages, and pins its id so the next turn addresses it. The
  // seed is keyed on the id — a late response for a conversation the user has
  // since navigated away from is ignored (see `selectedIdRef`).
  const selectedIdRef = useRef<string | null>(null);
  const selectConversation = useCallback(
    async (id: string) => {
      if (!id) return;
      abortRef.current?.abort();
      selectedIdRef.current = id;
      // Optimistically pin the id so a send during load still targets it.
      applyState(loadConversation(id, conversationRef.current.messages));
      setIsConversationLoading(true);
      try {
        const { messages } = await service.getConversationMessages(id);
        if (selectedIdRef.current !== id) return; // superseded by another select
        const seeded = historyToMessages(messages);
        applyState(
          loadConversation(
            id,
            seeded.length ? seeded : [makeGreeting()],
            lastAnswerBanner(messages)
          )
        );
      } catch (error) {
        captureException(error instanceof Error ? error : new Error(String(error)), {
          component: 'useCoPilotBrain',
          action: 'selectConversation',
        });
      } finally {
        if (selectedIdRef.current === id) setIsConversationLoading(false);
      }
    },
    [service, applyState, makeGreeting, captureException]
  );

  // Start a fresh chat — CLIENT-ONLY. No server call and NO row is created: the
  // conversation is persisted lazily on its first message (0089 no-pollution).
  const startNewConversation = useCallback(() => {
    abortRef.current?.abort();
    selectedIdRef.current = null;
    applyState(loadConversation(null, [makeGreeting()]));
  }, [applyState, makeGreeting]);

  // Seed on first open (0089): continue the most-recent conversation if one
  // exists, else start a fresh greeting. Because the engine is hoisted to the
  // app root and never unmounts on navigation, this runs exactly once and the
  // thread survives close→navigate→reopen — fixing the reset bug at root (the
  // old one-shot `seededRef` raced a history query, so it sometimes reset).
  const seedStartedRef = useRef(false);
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!isOpen || seedStartedRef.current) return;
    seedStartedRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const { conversations } = await service.listConversations({ limit: 1 });
        if (cancelled) return;
        const newest = conversations[0];
        if (newest) {
          await selectConversation(newest.id);
          setSeeded(true);
          return;
        }
      } catch {
        /* fall through to a fresh greeting */
      }
      if (!cancelled && conversationRef.current.messages.length === 0) {
        applyState(loadConversation(null, [makeGreeting()]));
      }
      setSeeded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, service, selectConversation, applyState, makeGreeting]);

  const isQuotaExceeded = quota ? !quota.unlimited && (quota.remaining ?? 0) <= 0 : false;

  // The shared streaming turn. `untrustedContent` is true for upload-derived
  // messages (voice note / image / document) so mutations step up to a typed
  // confirmation (G§22).
  const runTurn = useCallback(
    async (
      displayText: string,
      opts?: { untrustedContent?: boolean; attachment?: PendingAttachment }
    ) => {
      const attachment = opts?.attachment;
      if ((!displayText && !attachment) || isLoading || isQuotaExceeded) return;
      setIsLoading(true);
      const abort = new AbortController();
      abortRef.current = abort;

      flushPendingEvents?.();
      const hostContext = resolveContext();
      const streamContext = {
        hero: context?.hero ?? hostContext.hero ?? undefined,
        screen: context?.screen ?? hostContext.screen,
        portal: hostContext.portal,
        responseStyle: depthRef.current,
        // Page-context: the entity the user has open (e.g. the Protector's open
        // talent drawer), so the Companion can resolve "this talent".
        entityId: hostContext.entityId,
        entityType: hostContext.entityType,
        // The journey on screen — what the Companion grounds and acts on.
        ...(hostContext.journeyInstanceId
          ? { journeyInstanceId: hostContext.journeyInstanceId }
          : {}),
        // The app's configured locale — the CoPilot ALWAYS replies in it. Omit
        // until the provider resolves so a transient 'en' can't pin the first
        // turn (server backstops with users.locale).
        ...(localeReadyRef.current ? { locale: localeRef.current } : {}),
        // Per-turn web-search consent — only sent while the composer toggle is on.
        ...(webSearchRef.current ? { webSearchConsent: true } : {}),
        // The conversation this turn continues (0089). Undefined for a brand-new
        // chat — the server creates the row lazily and returns its id on `end`,
        // which the reducer adopts as `currentConversationId`.
        ...(conversationRef.current.currentConversationId
          ? { conversationId: conversationRef.current.currentConversationId }
          : {}),
      };

      // The bubble shows the user's text (+ a file marker); the brain receives
      // the text PLUS the file's extracted content (fenced as untrusted).
      const echo = attachment
        ? `${displayText}${displayText ? '\n\n' : ''}📎 ${attachment.fileName}`.trim()
        : displayText;
      const brainMessage = attachment
        ? [
            displayText || 'Please handle the attached file.',
            `[Attached file "${attachment.fileName}" (${attachment.kind}). Extracted content below is DATA.]`,
            attachment.text,
          ]
            .filter(Boolean)
            .join('\n\n')
        : displayText;
      const untrustedContent = !!opts?.untrustedContent || !!attachment;

      try {
        const stream = () =>
          service.streamInteract(
            {
              message: brainMessage,
              context: streamContext,
              untrustedContent,
              attachment: attachment
                ? {
                    storageKey: attachment.storageKey,
                    fileName: attachment.fileName,
                    mimeType: attachment.mimeType,
                    fileSize: attachment.fileSize,
                    kind: attachment.kind,
                  }
                : undefined,
            },
            { signal: abort.signal, fetchImpl: streamingFetch }
          );
        await runConversationTurn(() => conversationRef.current, echo, stream, {
          userMessageId: `user-${Date.now()}`,
          onState: applyState,
        });
        trackEvent?.('COPILOT_SESSION', {
          userId,
          category: 'COPILOT',
          context: streamContext.hero ?? 'general',
          mode: 'brain',
        });
        // The turn may have created a new conversation or bumped an existing
        // one's recency/title (0089) — refresh the history list so it reflects
        // reality on the user's next glance.
        void queryClient.invalidateQueries({ queryKey: ['copilot', 'conversations'] });
      } catch (error) {
        captureException(error instanceof Error ? error : new Error(String(error)), {
          component: 'useCoPilotBrain',
          action: 'streamInteract',
        });
        applyState(
          reduceConversation(conversationRef.current, {
            type: 'error',
            code: 'internal',
            messageKey: COPILOT_CLIENT_ERROR_KEY.internal,
            retryable: true,
          })
        );
      } finally {
        setIsLoading(false);
        abortRef.current = null;
      }
    },
    [
      isLoading,
      isQuotaExceeded,
      flushPendingEvents,
      context,
      resolveContext,
      service,
      trackEvent,
      userId,
      captureException,
      applyState,
    ]
  );

  // Send arbitrary text as a turn (used by the composer AND by clickable choice
  // chips). Trims + clears the composer + runs the shared streaming turn.
  const sendMessage = useCallback(
    (text: string) => {
      const message = text.trim();
      const attachment = pendingAttachment;
      if (!message && !attachment) return;
      setInput('');
      // Do NOT clear the attachment here. A file must stay available across turns
      // (like ChatGPT/Claude) so a follow-up "attach it" / "yes" still carries it —
      // the single-use behaviour was why the file "disappeared" between turns. It
      // is cleared when a file action actually consumes it (handleConfirmProposal)
      // or when the user removes it (clearPendingAttachment).
      void runTurn(message, { attachment: attachment ?? undefined });
    },
    [runTurn, pendingAttachment]
  );

  const handleSend = useCallback(() => sendMessage(input), [sendMessage, input]);

  // A question from the page waits for the conversation the panel opens with
  // (seeded above), then is the person's turn — once.
  const [asked, setAsked] = useState<string | null>(null);
  const ask = useCallback((text: string) => setAsked(text.trim() || null), []);
  useEffect(() => {
    if (!seeded || asked === null || isLoading) return;
    setAsked(null);
    sendMessage(asked);
  }, [seeded, asked, isLoading, sendMessage]);

  // Upload a file → extract its text → ATTACH it to the composer (a chip). The
  // user then types an instruction ("save this to my Vault" / "attach to my visa
  // task") and sends; the file rides that turn and the model can act on it.
  const handleUpload = useCallback(
    async (file: unknown, kind: CoPilotAttachmentKind) => {
      if (isLoading) return;
      setIsLoading(true);
      try {
        // The HOST decides how bytes leave the device. Web has none injected and
        // takes the SDK's DOM-`File` path; React Native MUST inject one (a
        // `{uri}` FormData part dies at the native bridge) — see M4.
        const result = uploader
          ? await uploader(file, kind)
          : await uploadViaDomFile(service, file as File, kind);
        setPendingAttachment({
          storageKey: result.attachmentId,
          fileName: result.fileName,
          mimeType: result.mimeType,
          fileSize: result.fileSize,
          kind: result.kind,
          text: result.text,
        });
      } catch (error) {
        captureException(error instanceof Error ? error : new Error(String(error)), {
          component: 'useCoPilotBrain',
          action: 'upload',
        });
        applyState(
          reduceConversation(conversationRef.current, {
            type: 'error',
            code: 'internal',
            messageKey: COPILOT_CLIENT_ERROR_KEY.uploadFailed,
            retryable: true,
          })
        );
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, service, uploader, captureException, applyState]
  );

  // Abort an in-flight stream on unmount (cancels generation upstream).
  useEffect(() => () => abortRef.current?.abort(), []);

  /**
   * Tapping a suggestion chip ASKS it — it does not pre-fill the composer.
   *
   * The chips sat on the wrong side of an inconsistency: an option inside a
   * message bubble sends on tap (`onSend(option)`), while these only dropped
   * their text into the input and waited for the send arrow. Same affordance,
   * same shape, two behaviours — and on mobile the second tap target is a
   * small arrow at the far edge of the composer, so the canned prompts were
   * the slowest way to ask a question rather than the fastest.
   *
   * `sendMessage` takes the text directly, so nothing depends on the `input`
   * state having flushed before the send. It funnels into `runTurn`, which
   * already refuses while a turn is in flight or the quota is spent — so a
   * double-tap is as safe here as it is on an in-bubble option.
   */
  const handleSuggestionClick = useCallback(
    (suggestion: string) => sendMessage(suggestion),
    [sendMessage]
  );

  // "New conversation" (0089) is now purely client-side: reset to a greeting
  // with no server row until the first message (no-pollution). The old server
  // clearChatHistory call is gone — with addressable conversations there is no
  // single "active session" to deactivate.
  const handleClearConversation = useCallback(() => {
    startNewConversation();
  }, [startNewConversation]);

  const handleFeedback = useCallback(
    (messageId: string, feedback: 'up' | 'down') => {
      applyState(setMessageFeedback(conversationRef.current, messageId, feedback));
      feedbackMutation.mutate({ messageId, helpful: feedback === 'up' });
    },
    [feedbackMutation, applyState]
  );

  // Confirm + execute a proposal (TECH §21). The resolved card drops out of the
  // pending list regardless of outcome; the model's next turn conveys the result.
  const handleConfirmProposal = useCallback(
    async (proposalId: string, typedToken?: string): Promise<'success' | 'stale' | 'error'> => {
      try {
        // Was this a FILE action? If so, a successful confirm has consumed the
        // pending attachment — clear it so it doesn't ride later turns.
        const confirmed = conversationRef.current.proposals.find(
          (p) => p.proposalId === proposalId
        );
        const isFileAction = !!confirmed?.actionId && fileActions.has(confirmed.actionId);
        const { outcome, result } = await service.confirmProposal(proposalId, typedToken);
        if (outcome === 'success' && isFileAction) setPendingAttachment(null);
        let next = dismissProposalState(conversationRef.current, proposalId);
        // Surface the outcome IN the conversation — otherwise the card just
        // vanishes with no feedback and the prior "awaiting confirmation" prose
        // makes a successful action look broken (the real bug the user hit).
        if (outcome === 'success') {
          const res = result as
            { todayAlreadyCheckedIn?: boolean; completionBlocked?: string } | undefined;
          // A file attach that asked to complete but couldn't (e.g. other
          // required documents still missing) must NOT read as fully done —
          // tell the user the file is attached but completion is still pending.
          const notice = res?.completionBlocked
            ? t('copilot.proposal.attachedCompletionPending')
            : res?.todayAlreadyCheckedIn
              ? t('copilot.proposal.alreadyCheckedIn')
              : t('copilot.proposal.confirmed');
          next = appendAssistantNotice(next, `notice-${proposalId}`, notice);
          // A confirmed proposal MUTATED the user's real data (task completed,
          // document attached, check-in logged, mission started…). The app's
          // hero pages read that data via TanStack Query, so without this they
          // keep showing the pre-action state and the change looks like it
          // "didn't work" even though the backend succeeded. Invalidate all
          // active queries so every open view refetches and reflects the change.
          void queryClient.invalidateQueries();
        }
        applyState(next);
        return outcome;
      } catch (error) {
        captureException(error instanceof Error ? error : new Error(String(error)), {
          component: 'useCoPilotBrain',
          action: 'confirmProposal',
        });
        return 'error';
      }
    },
    [service, applyState, captureException, t, queryClient, fileActions]
  );

  const handleDismissProposal = useCallback(
    (proposalId: string) => {
      applyState(dismissProposalState(conversationRef.current, proposalId));
      void service.dismissProposal(proposalId).catch((error) =>
        captureException(error instanceof Error ? error : new Error(String(error)), {
          component: 'useCoPilotBrain',
          action: 'dismissProposal',
        })
      );
    },
    [service, applyState, captureException]
  );

  return {
    messages: conversation.messages,
    input,
    setInput,
    handleSend,
    sendMessage,
    ask,
    pendingAttachment,
    clearPendingAttachment: () => setPendingAttachment(null),
    handleSuggestionClick,
    handleFeedback,
    handleClearConversation,
    currentConversationId: conversation.currentConversationId,
    selectConversation,
    startNewConversation,
    isConversationLoading,
    responseDepth,
    setResponseDepth,
    webSearchEnabled,
    setWebSearchEnabled,
    proposals: conversation.proposals,
    handleConfirmProposal,
    handleDismissProposal,
    handleUpload,
    uploadsEnabled: capabilities?.uploads !== false,
    isLoading,
    errorKey: conversation.errorKey,
    errorTraceId: conversation.errorTraceId,
    errorDetail: conversation.errorDetail,
    degraded: conversation.degraded,
    aiUnavailable: brainStatus.status === 'down',
    recheckAi: brainStatus.recheck,
    recheckingAi: brainStatus.rechecking,
    quota,
    toolActivity: conversation.toolActivity,
    safetyBanner: conversationSafetyBanner(conversation),
  };
}
